import { Injectable } from '@angular/core';
import { TSelectedApiVariable } from '@core/api/naviqore-data-api.service';

import { SystemTags } from '@dashboard/models';
import { TransformTagToId } from '@dashboard/tags-to-ids.transformer';

export type DataSeriesVariable = TSelectedApiVariable & {
  stateId: string;
} & ExtraPayloadProps;
type ApiSources = 'query_data' | 'custering_data';
type Source = {
  tag: SystemTags;
  stateId: string;
} & ExtraPayloadProps;
type ExtraPayloadProps = {
  extraPayloadProps?: any;
};

@Injectable({
  providedIn: 'root',
})
export class DataToApiService {
  private static SIDEBAR_DATA: Source[] = [
    {
      stateId: 'downsample_wind',
      tag: 'WIND_SPEED_REL_K',
    },
    {
      stateId: 'downsample_engine_speed',
      tag: 'ME_RPM',
    },
    {
      stateId: 'downsample_engine_power',
      tag: 'ME_POWER',
    },
    {
      stateId: 'downsample_vessel_speed',
      tag: 'SOG',
    },
  ];

  private static VESSEL_INFO_DATA: {
    apiSource: ApiSources;
    sources: Source[];
  }[] = [
    {
      apiSource: 'query_data',
      sources: [
        {
          stateId: 'chart_ME_Power',
          tag: 'ME_POWER',
        },
        {
          stateId: 'chart_ME_Power_Ref_HeavyProp',
          tag: 'ME_POWER_REF_HEAVY_PROP',
        },
        {
          stateId: 'chart_ME_Power_Ref_LightProp',
          tag: 'ME_POWER_REF_LIGHT_PROP',
        },
        {
          stateId: 'chart_speed_through_water',
          tag: 'STW',
        },
        {
          stateId: 'chart_speed_over_ground',
          tag: 'SOG',
        },
        {
          stateId: 'chart_ME_RPM',
          tag: 'ME_RPM',
        },
        {
          stateId: 'chart_Current',
          tag: 'CURRENT',
        },
        {
          stateId: 'chart_WindSpeedAbsBF',
          tag: 'WIND_SPEED_ABS_BF',
        },
        {
          stateId: 'chart_WIND_SPEED_ABS_M',
          tag: 'WIND_SPEED_ABS_M',
        },
        {
          stateId: 'chart_Slip',
          tag: 'SLIP',
        },
        //
        {
          stateId: 'chart_ME_FOC_TPD',
          tag: 'ME_FOC_TPD',
        },
        {
          stateId: 'chart_DG_FOC_TPD',
          tag: 'DG_FOC_TPD',
        },
        {
          stateId: 'chart_BOILER_FOC_TPD',
          tag: 'BOILER_FOC_TPD',
        },
        {
          stateId: 'chart_DraftAft',
          tag: 'DRAFT_AFT',
        },
        {
          stateId: 'chart_DraftFor',
          tag: 'DRAFT_FOR',
        },
        {
          stateId: 'chart_DraftMidP',
          tag: 'DRAFT_MID_P',
        },
        {
          stateId: 'chart_DraftMidS',
          tag: 'DRAFT_MID_S',
        },
        {
          stateId: 'chart_dg1_power',
          tag: 'DG1_POWER',
        },
        {
          stateId: 'chart_dg2_power',
          tag: 'DG2_POWER',
        },
        {
          stateId: 'chart_dg3_power',
          tag: 'DG3_POWER',
        },
        {
          stateId: 'chart_DG_FOC_TPD_24H',
          tag: 'DG_FOC_TPD_24H',
        },
        {
          stateId: 'chart_BOILER_FOC_TPD_24H',
          tag: 'BOILER_FOC_TPD_24H',
        },
        {
          stateId: 'chart_DG1_RUNNING_HOURS',
          tag: 'DG1_RUNNING_HOURS',
        },
        {
          stateId: 'chart_DG2_RUNNING_HOURS',
          tag: 'DG2_RUNNING_HOURS',
        },
        {
          stateId: 'chart_DG3_RUNNING_HOURS',
          tag: 'DG3_RUNNING_HOURS',
        },
      ],
    },
    {
      apiSource: 'custering_data',
      sources: [
        {
          stateId: 'bar_windspeed_bf',
          tag: 'WIND_SPEED_ABS_BF',
        },
        {
          stateId: 'bar_engine_speed',
          tag: 'ME_RPM',
        },
        {
          stateId: 'bar_sog',
          tag: 'SOG',
        },
        {
          stateId: 'pie_steaming_time',
          tag: 'OPERATIONAL_STATUS',
          extraPayloadProps: {
            transformation: { type: 'FORMATTED' },
          },
        },
      ],
    },
  ];

  public SidebarQueryPayload(): DataSeriesVariable[] {
    return DataToApiService.SIDEBAR_DATA.map((data) => {
      return {
        tagId: TransformTagToId(data.tag),
        stateId: data.stateId,
      };
    });
  }
  public WindowInfoDataQueryPayload(): DataSeriesVariable[] {
    return DataToApiService.VESSEL_INFO_DATA.filter(
      (api) => api.apiSource === 'query_data'
    )
      .map((api) => api.sources)
      .flat()
      .map((data) => {
        return {
          tagId: TransformTagToId(data.tag),
          stateId: data.stateId,
        };
      });
  }
  public WindowInfoClusteringQueryPayload(): DataSeriesVariable[] {
    const d = DataToApiService.VESSEL_INFO_DATA.filter(
      (api) => api.apiSource === 'custering_data'
    )
      .map((api) => api.sources)
      .flat()
      .map((data) => {
        return {
          tagId: TransformTagToId(data.tag),
          stateId: data.stateId,
          ...data,
        };
      });
    return d;
  }
}
